import { Link } from "react-router-dom";
import { useSiteSettings } from "@/services/siteSettings";

export default function Footer() {
  const { get } = useSiteSettings();
  const siteName = get("site_name", "Nomad Livings");
  const logoUrl = get("brand_logo_url", "");
  const phone = get("contact_phone", "80883 30330");
  const whatsapp = get("contact_whatsapp", phone);
  const email = get("contact_email", "");
  const address = get("contact_address", "#385, 13th Cross Road, Beside Bank of Baroda, Sadashivnagar, Aramane Nagar, Bengaluru – 560080, Karnataka.");
  const facebook = get("social_facebook", "");
  const instagram = get("social_instagram", "");
  const linkedin = get("social_linkedin", "");
  const waDigits = whatsapp.replace(/[^0-9]/g, "");
  const waLink = `https://wa.me/${waDigits.startsWith("91") ? waDigits : `91${waDigits}`}`;
  return (
    <footer className="relative w-full bg-primary-800 text-background-100">
      <div className="w-full px-4 md:px-8 max-w-7xl mx-auto py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={`${siteName} logo`}
                  className="w-10 h-10 rounded-full object-cover bg-background-50/10"
                />
              ) : (
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-accent-500 text-background-50">
                  <i className="ri-home-4-line text-lg"></i>
                </span>
              )}
              <span className="flex flex-col leading-none">
                <span className="font-heading font-semibold text-lg text-background-50 tracking-tight">
                  {siteName}
                </span>
                <span className="text-[10px] tracking-[0.28em] uppercase text-background-200/80">
                  A-Frame Villa Investment
                </span>
              </span>
            </Link>
            <p className="text-sm text-background-200/90 mt-5 leading-relaxed">
              Invest in premium modular A-frame villas designed for staycations, Airbnb rentals,
              boutique resorts and holiday homes — engineered for comfort, sustainability and
              strong returns.
            </p>
            <p className="font-heading italic text-accent-300 mt-5">
              Live Freely. Stay Meaningfully.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-bold text-background-50 uppercase tracking-wide">Investments</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-background-200/90">
              <li><Link to="/listings" className="hover:text-accent-300 transition-colors">Nomad One Cabin</Link></li>
              <li><Link to="/listings" className="hover:text-accent-300 transition-colors">Aurora Studio</Link></li>
              <li><Link to="/listings" className="hover:text-accent-300 transition-colors">Havenside 2 BHK</Link></li>
              <li><Link to="/listings" className="hover:text-accent-300 transition-colors">Slopeview Glass</Link></li>
              <li><Link to="/listings" className="hover:text-accent-300 transition-colors">Crestline Duplex</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-background-50 uppercase tracking-wide">Explore</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-background-200/90">
              <li><Link to="/why-invest" className="hover:text-accent-300 transition-colors">Why Invest</Link></li>
              <li><Link to="/listings" className="hover:text-accent-300 transition-colors">Villa Models</Link></li>
              <li><Link to="/returns" className="hover:text-accent-300 transition-colors">Income &amp; Returns</Link></li>
              <li><Link to="/resort" className="hover:text-accent-300 transition-colors">Resort Features</Link></li>
              <li><Link to="/models" className="hover:text-accent-300 transition-colors">Revenue Models</Link></li>
              <li><Link to="/contact" className="hover:text-accent-300 transition-colors">Schedule Site Visit</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-background-50 uppercase tracking-wide">Contact</h4>
            <ul className="mt-4 space-y-3 text-sm text-background-200/90">
              {phone && (
                <li>
                  <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 hover:text-accent-300 transition-colors">
                    <i className="ri-phone-fill text-accent-400"></i> {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-accent-300 transition-colors">
                    <i className="ri-mail-line text-accent-400"></i> {email}
                  </a>
                </li>
              )}
              {address && (
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-2-line text-accent-400"></i> {address}
                </li>
              )}
            </ul>
            {(facebook || instagram || linkedin) && (
              <div className="flex items-center gap-2 mt-5">
                {facebook && (
                  <a href={facebook} target="_blank" rel="nofollow noopener" aria-label="Facebook" className="flex items-center justify-center w-8 h-8 rounded-full bg-background-50/10 text-background-50 hover:bg-accent-500 transition-colors">
                    <i className="ri-facebook-fill text-sm"></i>
                  </a>
                )}
                {instagram && (
                  <a href={instagram} target="_blank" rel="nofollow noopener" aria-label="Instagram" className="flex items-center justify-center w-8 h-8 rounded-full bg-background-50/10 text-background-50 hover:bg-accent-500 transition-colors">
                    <i className="ri-instagram-line text-sm"></i>
                  </a>
                )}
                {linkedin && (
                  <a href={linkedin} target="_blank" rel="nofollow noopener" aria-label="LinkedIn" className="flex items-center justify-center w-8 h-8 rounded-full bg-background-50/10 text-background-50 hover:bg-accent-500 transition-colors">
                    <i className="ri-linkedin-fill text-sm"></i>
                  </a>
                )}
              </div>
            )}
            <a
              href={waLink}
              target="_blank"
              rel="nofollow noopener"
              className="inline-flex items-center gap-2 mt-5 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-500 text-background-50 px-5 py-2.5 hover:bg-accent-600 transition-colors"
            >
              <i className="ri-whatsapp-line"></i> WhatsApp Us
            </a>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-background-100/15 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-background-200/80">
          <p>© {new Date().getFullYear()} Nomad Livings · Govianu Group. All rights reserved.</p>
          <p>Developed by{" "}
            <a
              href="https://www.digicube.net.in"
              target="_blank"
              rel="nofollow noopener"
              className="font-semibold text-accent-400 hover:text-accent-300 underline-offset-4 hover:underline"
            >
              Digicube Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}